/**
 * useZoomToCoordinatesEffect
 *
 * Shared store-driven "fly the map to a coordinate" effect. Several map shells
 * subscribe to `map.zoomToCoordinates` (fed by go-to-my-location and artifact
 * list rows) and, when it changes, fly the MapLibre camera there and then clear
 * the request so it can fire again.
 *
 * The clear step is caller-owned via `onApplied` because not every consumer
 * resets the request (e.g. read-only mini-maps that only observe it).
 *
 * @module components/map/hooks/useZoomToCoordinatesEffect
 */
import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';

import type { Coordinates } from '@/types';

interface UseZoomToCoordinatesEffectParams {
  mapRef: RefObject<MapRef | null>;
  isMapReady: boolean;
  /** The pending zoom-to request from the store (`null` when none). */
  zoomToCoordinates: Coordinates | null;
  /** Target zoom level for the flight. */
  zoom: number;
  /** Camera animation duration in milliseconds. */
  durationMs: number;
  /**
   * Bottom region (px) occupied by an overlay panel (e.g. the mobile preview
   * sheet) — the camera is shifted up by half of it so the target frames above
   * the panel instead of being centered behind it. Mirrors `MapContainer`'s
   * `-bottomPadding / 2` offset. Default `0` (centered).
   */
  bottomPaddingPx?: number;
  /**
   * Delay (ms) before issuing the flight. Mirrors `performZoomToCoordinates`'s
   * 400ms delay so the flight starts AFTER a touch gesture settles — issuing
   * `flyTo` synchronously inside a `touchend` handler (mobile task-marker tap)
   * gets cancelled by the gesture tail. Default `0` (immediate).
   */
  delayMs?: number;
  /** Invoked after the flight is issued — typically clears the store request. */
  onApplied?: () => void;
}

export function useZoomToCoordinatesEffect({
  mapRef,
  isMapReady,
  zoomToCoordinates,
  zoom,
  durationMs,
  bottomPaddingPx = 0,
  delayMs = 0,
  onApplied,
}: UseZoomToCoordinatesEffectParams): void {
  useEffect(() => {
    if (!zoomToCoordinates || !isMapReady) return;

    // `flyTo` + `onApplied` run together after the delay (mirrors
    // `performZoomToCoordinates`): clearing the store request before the flight
    // would re-run this effect and the cleanup below would cancel the timer.
    const timer = window.setTimeout(() => {
      mapRef.current?.flyTo({
        center: [zoomToCoordinates.lng, zoomToCoordinates.lat],
        zoom,
        duration: durationMs,
        offset: [0, -bottomPaddingPx / 2],
      });
      onApplied?.();
    }, delayMs);

    return () => window.clearTimeout(timer);
    // Intentionally only `zoomToCoordinates`/`isMapReady`: `mapRef` is a stable
    // ref and `zoom`/`durationMs`/`delayMs` are constants at call sites. The
    // remaining values (`bottomPaddingPx`, `onApplied`) are read at fire time
    // from the latest render and travel with each fresh `zoomToCoordinates`
    // request, so re-subscribing the effect to them is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomToCoordinates, isMapReady]);
}
